import type { StudentSkillRow, StudentSkillsResponse } from "@edu/shared";
import {
  COLD_TEXT,
  CONSEQUENCES_HEAD,
  COVERAGE_NOTE,
  HELD_HEAD,
  HELD_TEXT,
  NEW_TOPIC_ACTION,
  PANEL_TITLE,
  SHEET_ACTION,
  THIN_TEXT,
  countedText,
  originText,
  unmeasuredText,
  viewOf,
} from "./student-skills-text.ts";
import { useStudentSkills } from "./use-student-skills.ts";
import { SkillLine } from "./StudentSkillsRow.tsx";
import { SkillsRoot } from "./StudentSkillsRoot.tsx";
import { DiagnosticSheetAction } from "./StudentSkillsSheet.tsx";

/**
 * Панель «Где дыры»: что у ученика не берётся и на чём это стоит (7.1
 * docs/student-errors-design.md).
 *
 * ⚠️ **Ни одного импорта из `@edu/shared/oge/*` ни в одном файле панели.**
 * Диагноз целиком считает сервер и отдаёт готовыми словами: 54 узла, 89 рёбер с
 * текстами и 29 рецептов утяжелили бы стартовый чанк всем, включая учеников,
 * ради одного экрана одной роли, а карточка ученика это НЕ ленивый чанк (1.4 и
 * 7.5 проекта). Проверяется это поиском маркерных строк графа в собранных
 * чанках, а не на глаз.
 *
 * ⚠️ **Стоит ВЫШЕ занятий** (1.6, развилка 12.5): ось продукта сменилась, и
 * «где у него дыры» это теперь главный вопрос карточки. Порядок обратим одной
 * строкой в `StudentPage.tsx`, и через месяц его стоит пересмотреть по тому,
 * листают панель или пользуются ею.
 *
 * ⚠️ **Ничего не красится тревожным цветом**: правило перенесено дословно из
 * `docs/student-marks-design.md`, раздел 4.
 *
 * ⚠️ **Ученику панель не показывается никогда** (7.4). Отдельного условия для
 * этого здесь нет и не нужно: ручка отвечает ученику 404, а карточка целиком
 * открывается только преподавательской стороне пары.
 */
export function StudentSkills(props: { studentUserId: string }) {
  const skills = useStudentSkills(props.studentUserId);

  if (skills.data === null) {
    // До приезда данных панель не занимает места вовсе: карточка не обязана
    // ждать самый тяжёлый запрос экрана, чтобы показать занятия
    if (skills.err === null) return null;
    return (
      <section className="card">
        <h2>{PANEL_TITLE}</h2>
        <p className="err">{skills.err}</p>
        <p>
          <button className="btn" type="button" onClick={skills.reload}>
            Повторить
          </button>
        </p>
      </section>
    );
  }

  const data = skills.data;

  return (
    <section className="card">
      <h2>
        {PANEL_TITLE}{" "}
        <span className="muted stu__count">{countedText(data.measured, data.window.days)}</span>
      </h2>

      {/* Отказ перечитывания встаёт НАД данными, а не вместо них: показанное
          уже прочитано, и убирать его из-за одной неудачной сети незачем */}
      {skills.err !== null && <p className="err">{skills.err}</p>}

      <Body data={data} studentUserId={props.studentUserId} />

      {/*
        ⚠️ Подпись про границу покрытия внизу и ВСЕГДА (3.2, случай В). Без неё
        преподаватель прочитает отсутствие геометрии как «с геометрией всё в
        порядке», а это самое дорогое из возможных недоразумений: заданий по ней
        у платформы нет ни одного, значит попасть в пробелы она не может физически.
      */}
      <p className="muted">{COVERAGE_NOTE}</p>
    </section>
  );
}

/** Тело панели по её состоянию (7.2 проекта). */
function Body(props: { data: StudentSkillsResponse; studentUserId: string }) {
  const { data } = props;
  const view = viewOf(data);

  if (view === "cold") {
    return (
      <>
        {/* Пусто это не пустой блок, а приглашение: у нового ученика попыток
            нет, и продукт обязан выходить из этого состояния одним нажатием */}
        <p>{COLD_TEXT}</p>
        <DiagnosticSheetAction
          studentUserId={props.studentUserId}
          topics={data.unmeasured}
          label={SHEET_ACTION}
        />
      </>
    );
  }

  if (view === "thin") {
    return (
      <>
        <p>{THIN_TEXT}</p>
        <ul className="list list--gaps">
          {data.skills.map((s) => (
            <SkillLine key={s.skill} row={s} />
          ))}
        </ul>
        <DiagnosticSheetAction
          studentUserId={props.studentUserId}
          topics={data.unmeasured}
          label={SHEET_ACTION}
        />
      </>
    );
  }

  if (view === "held") {
    return (
      <>
        <p>{HELD_TEXT}</p>
        <ul className="list list--gaps">
          {data.skills.map((s) => (
            <SkillLine key={s.skill} row={s} />
          ))}
        </ul>
        <Unmeasured data={data} />
        <DiagnosticSheetAction
          studentUserId={props.studentUserId}
          topics={data.unmeasured}
          label={NEW_TOPIC_ACTION}
        />
      </>
    );
  }

  return <Gaps data={data} studentUserId={props.studentUserId} />;
}

/**
 * Раскладка с найденным местом: сверху корни, под ними следствия, ниже то, что
 * держится.
 *
 * Порядок строк это порядок ответа на вопрос, а не важность: сначала «где
 * ломается», потом «что из этого следует», и только потом «что в порядке».
 */
function Gaps(props: { data: StudentSkillsResponse; studentUserId: string }) {
  const { data } = props;

  const rowOf = new Map<string, StudentSkillRow>(data.skills.map((s) => [s.skill, s]));
  const nameOf = (slug: string): string => rowOf.get(slug)?.name ?? slug;
  const isRoot = new Set(data.roots.map((r) => r.skill));

  const failed = data.skills.filter((s) => s.failed);
  const consequences = failed.filter((s) => !isRoot.has(s.skill));
  const held = data.skills.filter((s) => !s.failed);

  return (
    <>
      {data.roots.map((r) => (
        <SkillsRoot
          key={r.skill}
          root={r}
          row={rowOf.get(r.skill)}
          fromNames={r.from.map(nameOf)}
          rootWord={data.rootWord}
          studentUserId={props.studentUserId}
        />
      ))}

      {consequences.length > 0 && (
        <>
          <h3 className="gaps__head">{CONSEQUENCES_HEAD}</h3>
          <ul className="list list--gaps">
            {consequences.map((s) => (
              <SkillLine
                key={s.skill}
                row={s}
                // Откуда идёт: без этих слов три темы читаются как три задачи,
                // хотя задача одна
                note={originText(
                  data.roots.filter((r) => r.from.includes(s.skill)).map((r) => nameOf(r.skill)),
                )}
              />
            ))}
          </ul>
        </>
      )}

      {held.length > 0 && (
        <>
          {/*
            Блок «Держится» это не отчёт, а способ не дать прочитать отсутствие
            строки как провал. Числа и слово надёжности у него те же, что у
            остальных: требование «знаменатель у каждой строки» исключений не
            знает, а «неверно 0 из 6, уверенно» и «неверно 0 из 1,
            предположение» это два очень разных сообщения.
          */}
          <h3 className="gaps__head">{HELD_HEAD}</h3>
          <ul className="list list--gaps">
            {held.map((s) => (
              <SkillLine key={s.skill} row={s} />
            ))}
          </ul>
        </>
      )}

      <Unmeasured data={data} />

      <DiagnosticSheetAction
        studentUserId={props.studentUserId}
        topics={data.unmeasured}
        label={SHEET_ACTION}
      />
    </>
  );
}

/**
 * «Не проверяли»: темы, по которым задания у платформы есть, а ученику их не
 * давали.
 *
 * ⚠️ Блок обязателен и стоит перед кнопкой: он объясняет, зачем эта кнопка.
 * Отсутствие строки читается как «тут всё хорошо», и держать непроверенное
 * молчанием значит соврать ровно там, где мы честнее всего.
 */
function Unmeasured(props: { data: StudentSkillsResponse }) {
  if (props.data.unmeasured.length === 0) return null;

  return (
    <p className="muted">{unmeasuredText(props.data.unmeasured.map((t) => t.name))}</p>
  );
}
